package com.example.mystybag.login.logic

import com.example.mystybag.biometricUtils.expected.BiometricsHandler
import com.example.mystybag.biometricUtils.helpers.BiometricAuthenticationType
import com.example.mystybag.biometricUtils.helpers.BiometricErrorResult
import com.example.mystybag.common.logic.NavigatorMerchantBiometric
import com.example.mystybag.common.logic.NavigatorMerchantForgotPasswordEmail
import com.example.mystybag.login.logic.NavigatorMerchantRegistrationStep1
import com.example.mystybag.common.resources.SR
import com.example.mystybag.communication.Communication
import com.example.mystybag.communication.common.CommonCommands
import com.example.mystybag.communication.utils.Response
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.login.logic.NavigatorAddLocationPrompt
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_CODE
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_EMAIL
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_JWT
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_ID
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_EMAIL
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_IS_STAFF
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_STAFF_LOCATION_IDS
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_JWT
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_PASSWORD
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_USE_BIOMETRIC_FOR_MERCHANT
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_BIOMETRIC_PROMPT_DECLINED
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_USER_ID
import com.example.mystybag.core.expected.Preferences
import com.example.mystybag.core.helpers.GlobalCommonData.getReferralCodeValue
import com.example.mystybag.core.security.Security
import com.example.mystybag.core.expected.sharedActivity
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.extensions.isValidEmail
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.database.communication.db.DatabaseProvider
import com.example.mystybag.login.model.MerchantLoginUIState
import com.example.mystybag.sharedmodels.base.BaseResponseModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.GetMerchantDashboardRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.LoginMerchantRequestModel
import com.example.mystybag.sharedmodels.project.responseModels.merchant.GetMerchantDashboardResponseModel
import com.example.mystybag.sharedmodels.defines.MerchantStatusDefines.Companion.MERCHANT_STATUS_UNDER_REVIEW
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch

class MerchantLoginViewModel(
    context: AppContext
) : ChildViewModel<MerchantLoginUIState>(context, MerchantLoginUIState()) {

    private var autoBiometricTried: Boolean = false
    
    override fun onCreate() {
        navControls.backButtonVisible = true
        overrideBackButton = true
        navControls.title = "" // Title "Sign in" is shown in content to avoid overlap

        val storedEmail = Preferences.getString(key = PREFERENCES_MERCHANT_EMAIL, "")
        if (storedEmail.isNotEmpty() && uiState.value.email.isEmpty()) {
            updateState { it.copy(email = storedEmail) }
        }

        val biometricType = BiometricsHandler.getAuthenticationType()
        val biometricAvailable =
            biometricType != BiometricAuthenticationType.NONE && BiometricsHandler.isBiometricScannerAvailableAndSet()
        val useBiometricForMerchant = Preferences.getBoolean(key = PREFERENCES_USE_BIOMETRIC_FOR_MERCHANT, false)
        val hasSavedPassword = Preferences.getString(key = PREFERENCES_MERCHANT_PASSWORD, "").isNotEmpty()

        val showBiometricLogin =
            useBiometricForMerchant && biometricAvailable && storedEmail.isNotEmpty() && hasSavedPassword

        updateState {
            it.copy(
                biometricType = biometricType,
                showBiometricLogin = showBiometricLogin
            )
        }

        // Auto-try biometric once when available; fallback is manual password entry.
        if (showBiometricLogin && !autoBiometricTried) {
            autoBiometricTried = true
            onBiometricLoginClicked()
        }
    }
    
    fun onEmailChange(email: String) {
        updateState {
            it.copy(email = email, emailIsError = false, emailErrorMsg = "")
        }
    }
    
    fun onPasswordChange(password: String) {
        updateState {
            it.copy(password = password, passwordIsError = false, passwordErrorMsg = "")
        }
    }
    
    fun togglePasswordVisibility() {
        updateState {
            it.copy(passwordVisible = !it.passwordVisible)
        }
    }
    
    override fun onBackButtonClicked() {
        replaceAll(NavigatorWelcomeFromBack)
    }

    fun onForgotPasswordClicked() {
        navigate(NavigatorMerchantForgotPasswordEmail)
    }

    fun onCreateAccountClicked() {
        navigate(
            NavigatorMerchantRegistrationStep1(
                sharedConfig = MerchantRegistrationSharedConfig(
                    referralCode = getReferralCodeValue()
                )
            )
        )
    }
    
    fun onLoginClicked() {
        if (validate()) {
            performLogin(email = uiState.value.email, password = uiState.value.password)
        }
    }
    
    private fun validate(): Boolean {
        var isValid = true
        val state = uiState.value
        
        if (state.email.isEmpty() || !state.email.isValidEmail) {
            updateState {
                it.copy(
                    emailIsError = true,
                    emailErrorMsg = sharedString(SR.strings.please_enter_valid_email_address)
                )
            }
            isValid = false
        }
        
        if (state.password.isEmpty()) {
            updateState {
                it.copy(
                    passwordIsError = true,
                    passwordErrorMsg = sharedString(SR.strings.please_enter_valid_email_address) // TODO: Add proper password error message
                )
            }
            isValid = false
        }
        
        return isValid
    }
    
    fun onBiometricLoginClicked() {
        scope.launch {
            if (uiState.value.isLoading) return@launch

            val storedEmail = Preferences.getString(key = PREFERENCES_MERCHANT_EMAIL, "")
            val hasSavedPassword = Preferences.getString(key = PREFERENCES_MERCHANT_PASSWORD, "").isNotEmpty()
            if (storedEmail.isEmpty() || !hasSavedPassword) {
                launchOnMain { showSnackbar(sharedString(SR.strings.please_log_in_again)) }
                return@launch
            }

            val biometricAvailable =
                uiState.value.biometricType != BiometricAuthenticationType.NONE &&
                    BiometricsHandler.isBiometricScannerAvailableAndSet()

            if (!biometricAvailable) {
                launchOnMain { showSnackbar(sharedString(SR.strings.an_error_occurred_please_try_again_later)) }
                return@launch
            }

            launchOnMain {
                val started = BiometricsHandler.startBiometricAuthentication(
                    activity = sharedActivity,
                    onSuccess = {
                        val storedPassword = Preferences.getSecureString(key = PREFERENCES_MERCHANT_PASSWORD, defaultValue = "")
                        if (storedPassword.isEmpty()) {
                            showSnackbar(sharedString(SR.strings.please_log_in_again))
                        } else {
                            performLogin(email = storedEmail, password = storedPassword)
                        }
                    },
                    onFailed = { error ->
                        when (error) {
                            BiometricErrorResult.USER_CANCELED -> Unit // allow manual password entry
                            else -> showSnackbar(sharedString(SR.strings.an_error_occurred_please_try_again_later))
                        }
                    }
                )

                if (!started) {
                    showSnackbar(sharedString(SR.strings.an_error_occurred_please_try_again_later))
                }
            }
        }
    }

    private fun clearClientLoginState() {
        Preferences.remove(PREFERENCES_EMAIL)
        Preferences.remove(PREFERENCES_CODE)
        Preferences.remove(PREFERENCES_USER_ID)
        Preferences.remove(PREFERENCES_JWT)
    }

    /** After merchant login: if no locations, go directly to AddLocationPrompt; otherwise go to main (Dashboard). */
    private fun decideMerchantDestinationAndGo(merchantId: Long) {
        scope.launch {
            commandBlock(showProgress = false, hideProgress = false) {
                CommonCommands.getMerchantDashboard(GetMerchantDashboardRequestModel(merchantId = merchantId))
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                val dashboard = response as? GetMerchantDashboardResponseModel
                                val locations = dashboard?.locations ?: emptyList()
                                if (locations.isEmpty()) {
                                    setResult<LoginInterface> {
                                        goToMainScreen(
                                            false,
                                            null,
                                            NavigatorAddLocationPrompt(
                                                merchantId = merchantId,
                                                companyPhoneNumber = dashboard?.companyPhoneNumber,
                                                companyDialCode = dashboard?.companyDialCode,
                                                registeredCompanyStreet = dashboard?.registeredCompanyStreet,
                                                registeredCompanyCityId = dashboard?.registeredCompanyCityId,
                                                registeredCompanyCityName = dashboard?.registeredCompanyCityName
                                            )
                                        )
                                    }
                                } else {
                                    setResult<LoginInterface> { goToMainScreen(false) }
                                }
                            }
                        },
                        baseError = { _ ->
                            launchOnMain { setResult<LoginInterface> { goToMainScreen(false) } }
                        }
                    )
            }
        }
    }

    private fun performLogin(email: String, password: String) {
        scope.launch {
            updateState { it.copy(isLoading = true) }

            Preferences.setString(PREFERENCES_MERCHANT_JWT, "")
            Preferences.setLong(PREFERENCES_MERCHANT_ID, 0L)
            Communication.token = ""

            commandBlock(showProgress = true) {
                CommonCommands.loginMerchant(
                    LoginMerchantRequestModel(
                        email = email,
                        password = Security.hashPasswordForRequest(password)
                    )
                )
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                updateState { it.copy(isLoading = false) }

                                if (response.merchantStatus == MERCHANT_STATUS_UNDER_REVIEW) {
                                    navigate(NavigatorMerchantRegistrationUnderReview)
                                    return@launchOnMain
                                }

                                Communication.token = response.jwt
                                Preferences.setString(PREFERENCES_MERCHANT_EMAIL, email)
                                // Save password only after user enables biometric and passes auth (see MerchantBiometricViewModel).

                                clearClientLoginState()
                                CoroutineScope(Dispatchers.Default).launch {
                                    DatabaseProvider.clearDatabase()
                                }

                                val biometricAvailable =
                                    uiState.value.biometricType != BiometricAuthenticationType.NONE &&
                                        BiometricsHandler.isBiometricScannerAvailableAndSet()
                                val useBiometricForMerchant = Preferences.getBoolean(
                                    key = PREFERENCES_USE_BIOMETRIC_FOR_MERCHANT,
                                    defaultValue = false
                                )
                                val biometricPromptDeclined = Preferences.getBoolean(
                                    key = PREFERENCES_MERCHANT_BIOMETRIC_PROMPT_DECLINED,
                                    defaultValue = false
                                )

                                if (!useBiometricForMerchant && biometricAvailable && !biometricPromptDeclined) {
                                    // Persist session (JWT etc.) and show biometric offer once. Pass session in config so biometric screen does not rely on Preferences/isJwtExpired on iOS.
                                    Preferences.setString(PREFERENCES_MERCHANT_JWT, response.jwt)
                                    Preferences.setLong(PREFERENCES_MERCHANT_ID, response.merchantId)
                                    Preferences.setBoolean(PREFERENCES_IS_STAFF, false)
                                    Preferences.setString(PREFERENCES_STAFF_LOCATION_IDS, "")
                                    navigate(NavigatorMerchantBiometric(passwordToSaveAfterBiometric = password, jwt = response.jwt, merchantId = response.merchantId))
                                } else {
                                    // Set JWT in Preferences for this session so MainViewModel shows merchant UI (not client).
                                    // Next app open: WelcomeViewModel will clear JWT when useBiometricForMerchant is false and show merchant login.
                                    Preferences.setString(PREFERENCES_MERCHANT_JWT, response.jwt)
                                    Preferences.setLong(PREFERENCES_MERCHANT_ID, response.merchantId)
                                    Preferences.setBoolean(PREFERENCES_IS_STAFF, false)
                                    Preferences.setString(PREFERENCES_STAFF_LOCATION_IDS, "")
                                    decideMerchantDestinationAndGo(response.merchantId)
                                }
                            }
                        },
                        baseError = { response ->
                            launchOnMain {
                                updateState { it.copy(isLoading = false) }
                                showSnackbar(Response.getErrorMessage(response.status, response.apiEndPoint, response.statusMessage))
                            }
                        }
                    )
            }
        }
    }
}

